/**
 * The report's chart palette, in one place (2026-09-08).
 *
 * Every chart in the benchmark report draws from these constants so that a
 * colour means the same thing everywhere: "Citations" is the same orange in the
 * signal panels as in the per-model chart, and "Supplier Content" is the same
 * blue in the donut as in the per-model source mix. Defining them per-component
 * is how that guarantee gets lost.
 *
 * Values are the `dataviz` reference categorical palette, DARK steps, validated
 * against this app's real dark surface (#141b2e) rather than the skill's default:
 *
 *   node scripts/validate_palette.js "#3987e5,#d95926,#199e70,#c98500,#d55181,#008300" \
 *     --mode dark --surface "#141b2e"
 *   -> ALL CHECKS PASS (worst adjacent CVD dE 8.4, normal-vision 19.3, all >=3:1)
 *
 * Slots are assigned in fixed order and NEVER cycled or generated. A category
 * past the sixth folds into "Other" — see `sourceTypeShare` in @aurora/shared.
 */

/** Categorical slots 1-6 (dark steps). Order is the CVD-safety mechanism. */
export const CATEGORICAL = [
  '#3987e5',
  '#d95926',
  '#199e70',
  '#c98500',
  '#d55181',
  '#008300',
] as const;

/**
 * The three signals, fixed for the life of the report. Mention/Citation/
 * Recommendation take slots 1-3, which are the three that validate on the
 * strict all-pairs pairlist in dark mode.
 */
export const SIGNAL_HUES = {
  mention: CATEGORICAL[0],
  citation: CATEGORICAL[1],
  recommendation: CATEGORICAL[2],
} as const;

/**
 * De-emphasis grey for context marks — competitors in an emphasis chart, the
 * "everyone else" rows. Deliberately NOT a categorical slot: it must never read
 * as a series identity.
 */
export const CONTEXT = '#4a5670';

/** The client's own accent in emphasis charts. */
export const TARGET = CATEGORICAL[0];

/**
 * Ordinal ramp for the prompt-outcome heatmap. Mention -> Citation ->
 * Recommendation is an ORDERED scale of strength, so it takes ONE hue stepped
 * by lightness, never three categorical hues (which would imply the outcomes
 * are unrelated kinds rather than degrees of the same thing).
 *
 * On a dark surface "stronger" reads as brighter, so the ramp ascends.
 * Validated with the ordinal gate against this app's surface:
 *   node scripts/validate_palette.js "#1c5cab,#3987e5,#86b6ef" \
 *     --ordinal --mode dark --surface "#141b2e"   -> ALL CHECKS PASS
 *
 * `no_mention` is deliberately NOT on the ramp. Absence is not a strength
 * level, and a first attempt that gave it a near-surface step FAILED the
 * light-end contrast check at 1.17:1 — which was the right catch twice over:
 * on this run 94 of 104 cells are no-mention, so a ramp step would have painted
 * the whole grid a colour that says "something happened". It renders as an
 * empty outlined cell instead, and `not_scored` as a muted one.
 */
export const OUTCOME_RAMP = {
  mention: '#1c5cab',
  citation: '#3987e5',
  recommendation: '#86b6ef',
} as const;

/** Non-outcomes: absence and unusable. Never on the ramp. */
export const OUTCOME_EMPTY = 'transparent';
export const OUTCOME_NOT_SCORED = '#2a3348';

/** Pick a categorical slot by index, folding anything past the last into it. */
export function slot(i: number): string {
  return CATEGORICAL[Math.min(i, CATEGORICAL.length - 1)] as string;
}
